import {
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  Grid,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { Container } from "@mui/system";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Email from "@mui/icons-material/Email";
import AccountBalance from "@mui/icons-material/AccountBalance";
import Code from "@mui/icons-material/Code";
import { useLocation, useNavigate } from "react-router-dom";
import CroboContainer from "../../components/croboContainer";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateRecipient } from "../../reducers/userReducer";
import PeopleIcon from "@mui/icons-material/People";
import CustomSnackbar from "../../components/CustomSnackbar";

const EditRecipient = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [name, setName] = useState(state.nickName || "");
  const [accountHolderName, setAccountHolderName] = useState(
    state.name || state.nickName
  );
  const [email, setEmail] = useState(state.email || "");
  const [ifsc, setIfsc] = useState(state.ifsc || "");
  const [accountNo, setAccountNo] = useState(state.bank_account_number || "");
  const [relationship, setRelationship] = useState(state.relationship || "");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // can be 'success', 'warning', 'error', etc.
  });

  const [errors, setErrors] = useState({}); // Track validation errors

  // Validation function
  const validateFields = () => {
    const errors = {};

    // Name validation (no special characters except spaces, hyphens, and apostrophes)
    if (!name.trim()) {
      errors.name = "Account holder name is required.";
    } else if (!/^[A-Za-z\s'-]+$/.test(name)) {
      errors.name = "Name cannot contain special characters.";
    }

    const validateEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());
    };

    if (!validateEmail(email)) {
      errors.email = "Invalid email address.";
    }

    // Relationship validation
    if (!relationship) {
      errors.relationship = "Relationship is required.";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(
        updateRecipient({
          id: state.id,
          email: email,
          nickName: name,
          relationship: relationship,
        })
      ).unwrap();

      // Show success message
      setSnackbar({
        open: true,
        message: response.message || "Recipient updated successfully",
        severity: "success",
      });

      setTimeout(() => {
        navigate("/manage-recipients");
      }, 2000);

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update recipient",
        severity: "warning",
      });

      setTimeout(() => {
        navigate("/manage-recipients");
      }, 2000);

      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  return (
    <CroboContainer>
      <Box position={"relative"}>
        <Container
          sx={{
            marginTop: 4,
            width: "100%",
            minWidth: "320px",
          }}
        >
          <Typography variant="h6" align="left" gutterBottom>
            Edit recipient details
          </Typography>
          <Typography sx={{ color: "grey.600" }} variant="body2" gutterBottom>
            Note: NRE accounts are not supported currently.
          </Typography>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Account holder name"
              variant="outlined"
              value={accountHolderName}
              disabled
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "background.paper" }}
            />
            <TextField
              label="Account number"
              variant="outlined"
              value={accountNo}
              disabled
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalance />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "background.paper" }}
            />
            <TextField
              label="IFSC code"
              variant="outlined"
              value={ifsc}
              disabled
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Code />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "background.paper" }}
            />
            <TextField
              label="Email"
              variant="outlined"
              value={email}
              required
              error={!!errors.email}
              helperText={errors.email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "background.paper" }}
            />
            <TextField
              label="Nickname"
              variant="outlined"
              value={name}
              required
              error={!!errors.name}
              helperText={errors.name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "background.paper" }}
            />
            <TextField
              label="Relationship"
              id="relationship"
              value={relationship}
              sx={{ bgcolor: "background.paper" }}
              displayEmpty
              error={!!errors.relationship}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PeopleIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setRelationship(e.target.value)}
              select
            >
              <MenuItem value="Myself">Myself</MenuItem>
              <MenuItem value="My family">My family</MenuItem>
              <MenuItem value="Someone else">Someone else</MenuItem>
            </TextField>
            {errors.relationship && (
              <Typography variant="caption" color="error">
                {errors.relationship}
              </Typography>
            )}
            <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
              <Button
                variant="text"
                type="button"
                sx={{ color: "grey.600", textTransform: "none" }}
                onClick={() => navigate("/manage-recipients")}
              >
                Cancel
              </Button>
              <Button
                color="brand"
                disabled={loading}
                variant="contained"
                onClick={handleSubmit}
                sx={{ textTransform: "none" }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </CroboContainer>
  );
};

export default EditRecipient;
